import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Loader2,
  Users,
  Video,
  ChevronRight,
  CheckCircle2,
  Mail,
  Upload,
} from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { Badge, Button, Table } from "../components/ui";
import {
  fetchZoomEvents,
  fetchWebcastCampaigns,
  fetchVideoTemplate,
  getVideoTemplateToken,
  saveVideoTemplate,
  fmtDate,
} from "../services/api";
import { STATUS_MAP } from "../features/events/constants";
import CreateEventModal from "../features/events/CreateEventModal";
import EventDrawer from "../features/events/EventDrawer";




export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [ghlWebcasts, setGhlWebcasts] = useState([]);
  const [hasTemplate, setHasTemplate] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);

  const loadEvents = () => {
    setLoading(true);
    setError(null);
    fetchZoomEvents({ all: showAll })
      .then((data) => setEvents(data.events || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadEvents();
  }, [showAll]);

  // Load GHL campaigns that match webcast keywords
  useEffect(() => {
    fetchWebcastCampaigns()
      .then((data) => setGhlWebcasts(data.campaigns || []))
      .catch(() => {});
  }, []);

  // Check if an intro video template is uploaded
  useEffect(() => {
    fetchVideoTemplate()
      .then((data) => setHasTemplate(!!data.url))
      .catch(() => {});
  }, []);

  const handleTemplateUpload = useCallback(async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "video/mp4,video/webm,video/quicktime";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 100 * 1024 * 1024) {
        alert("Template video must be under 100 MB");
        return;
      }
      setUploadingTemplate(true);
      try {
        const { sasUrl, blobName } = await getVideoTemplateToken(
          file.name,
          file.type,
        );
        await fetch(sasUrl, {
          method: "PUT",
          headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type },
          body: file,
        });
        await saveVideoTemplate(blobName, file.name, file.type);
        setHasTemplate(true);
      } catch (err) {
        alert(`Upload failed: ${err.message}`);
      } finally {
        setUploadingTemplate(false);
      }
    };
    input.click();
  }, []);

  const columns = [
    {
      key: "title",
      label: "Webcast",
      sortValue: (row) => (row.title || "").toLowerCase(),
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(99,102,241,0.08)",
            }}
          >
            <Video size={16} color={colors.info} />
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>{row.title}</div>
            {row.description && (
              <div
                style={{
                  fontSize: 12,
                  color: colors.textSecondary,
                  maxWidth: 300,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.description}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "date",
      label: "Date",
      sortValue: (row) => row.scheduledAt ? new Date(row.scheduledAt).getTime() : 0,
      render: (row) => {
        const d = new Date(row.scheduledAt);
        return (
          <div>
            <div style={{ fontFamily: fonts.mono, fontSize: 12 }}>
              {fmtDate(row.scheduledAt)}
            </div>
            <div style={{ fontSize: 11, color: colors.textSecondary }}>
              {d.toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </div>
        );
      },
    },
    {
      key: "duration",
      label: "Duration",
      sortValue: (row) => row.durationMinutes || 0,
      render: (row) => (
        <span
          style={{
            fontSize: 12,
            color: colors.textSecondary,
            fontFamily: fonts.mono,
          }}
        >
          {row.durationMinutes ? `${row.durationMinutes}m` : "—"}
        </span>
      ),
    },
    {
      key: "invited",
      label: "Invited",
      sortValue: (row) => row.invitationCount || 0,
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Mail size={13} color={colors.textSecondary} />
          <span style={{ fontFamily: fonts.mono, fontSize: 12 }}>
            {row.invitationCount || 0}
          </span>
        </div>
      ),
    },
    {
      key: "registrations",
      label: "Registered",
      sortValue: (row) => row.registrationCount || 0,
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Users size={13} color={colors.textSecondary} />
          <span style={{ fontFamily: fonts.mono, fontSize: 12 }}>
            {row.registrationCount || 0}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortValue: (row) => (row.status || "").toLowerCase(),
      render: (row) => {
        const s = STATUS_MAP[row.status] || STATUS_MAP.scheduled;
        return (
          <Badge color={s.color} bg={s.bg}>
            {s.label}
          </Badge>
        );
      },
    },
    {
      key: "arrow",
      label: "",
      render: () => <ChevronRight size={14} color={colors.textSecondary} />,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: 28,
              fontWeight: 700,
              color: colors.text,
              marginBottom: 4,
            }}
          >
            Webcasts
          </h1>
          <p
            style={{
              fontFamily: fonts.body,
              fontSize: 14,
              color: colors.textSecondary,
            }}
          >
            {loading
              ? "Loading..."
              : `${events.length} webcast${events.length !== 1 ? "s" : ""}`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button
            variant="default"
            size="sm"
            icon={uploadingTemplate ? Loader2 : Upload}
            onClick={handleTemplateUpload}
            disabled={uploadingTemplate}
          >
            {uploadingTemplate
              ? "Uploading..."
              : hasTemplate
                ? "Replace Intro Video"
                : "Upload Intro Video"}
          </Button>
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setShowCreate(true)}
          >
            Create Webcast
          </Button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {[
          { id: false, label: "Upcoming" },
          { id: true, label: "All Webcasts" },
        ].map((f) => (
          <button
            key={String(f.id)}
            onClick={() => setShowAll(f.id)}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              border: `1px solid ${showAll === f.id ? colors.accent : colors.border}`,
              background: showAll === f.id ? colors.accentDim : "transparent",
              color: showAll === f.id ? colors.accent : colors.textSecondary,
              fontSize: 12,
              fontFamily: fonts.body,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div
          style={{
            padding: 16,
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 8,
            marginBottom: 16,
            color: "#ef4444",
            fontSize: 13,
          }}
        >
          Failed to load events: {error}
        </div>
      )}

      <Table columns={columns} data={events} onRowClick={setSelectedEvent} />

      {/* GHL Webcast History — only on "All Webcasts" tab */}
      {showAll && ghlWebcasts.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h2
            style={{
              fontFamily: fonts.heading,
              fontSize: 18,
              fontWeight: 700,
              color: colors.text,
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Mail size={18} color={colors.accent} />
            Past Webcast Campaigns (GHL)
          </h2>
          <Table
            columns={[
              {
                key: "name",
                label: "Campaign",
                sortValue: (row) => (row.name || "").toLowerCase(),
                render: (row) => (
                  <div>
                    <div style={{ fontWeight: 600 }}>{row.name}</div>
                    {row.subject && row.subject !== row.name && (
                      <div
                        style={{
                          fontSize: 12,
                          color: colors.textSecondary,
                          marginTop: 2,
                        }}
                      >
                        {row.subject}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: "date",
                label: "Date",
                sortValue: (row) => row.sentAt ? new Date(row.sentAt).getTime() : 0,
                render: (row) => (
                  <span
                    style={{
                      fontFamily: fonts.mono,
                      fontSize: 12,
                      color: colors.textSecondary,
                    }}
                  >
                    {row.sentAt ? fmtDate(row.sentAt) : "—"}
                  </span>
                ),
              },
              {
                key: "recipients",
                label: "Delivered",
                sortValue: (row) => row.recipients || 0,
                render: (row) => (
                  <span style={{ fontFamily: fonts.mono, fontSize: 12 }}>
                    <span style={{ color: colors.success }}>
                      {row.recipients.toLocaleString()}
                    </span>
                    {" / "}
                    <span style={{ color: colors.textSecondary }}>
                      {row.totalCount.toLocaleString()}
                    </span>
                    {row.failed > 0 && (
                      <span style={{ color: colors.danger, marginLeft: 4 }}>
                        ({row.failed} failed)
                      </span>
                    )}
                  </span>
                ),
              },
              {
                key: "status",
                label: "Status",
                render: (row) => (
                  <Badge color={colors.success} bg={`${colors.success}18`}>
                    <CheckCircle2 size={10} /> {row.status}
                  </Badge>
                ),
              },
            ]}
            data={ghlWebcasts}
          />
        </div>
      )}

      {showCreate && (
        <CreateEventModal
          onClose={() => setShowCreate(false)}
          onCreated={(ev) => {
            setEvents((prev) => [ev, ...prev]);
            setShowCreate(false);
          }}
        />
      )}

      <EventDrawer
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onCancelled={(id) => {
          setEvents((prev) =>
            prev.map((e) => (e.id === id ? { ...e, status: "cancelled" } : e)),
          );
          setSelectedEvent(null);
        }}
      />
    </div>
  );
}